import React, { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Add, DeleteOutlined, SearchOutlined } from '@mui/icons-material';
import { IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { toast } from 'react-toastify';
import API from 'api/API';
import { User } from 'api/Endpoints/UserManagementEndpoint';
import Theme from 'App.theme';
import Checkbox from 'components/Checkbox/Checkbox';
import CreateUserModal from 'components/CreateUserModal/CreateUserModal';
import DeleteUserModal from 'components/DeleteUserModal/DeleteUserModal';
import DropDown from 'components/DropDown/DropDown';
import InputField from 'components/InputField';
import Page from 'components/Page';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { TableContainer } from 'components/Table/Table';
import { useLanguage } from 'context/AppContext';
import { Role, userRolesIndex } from 'store/user/auth0.helpers';
import { useUser } from 'store/user/useUser';
import { isInMaxKelsenOrg, isJnJAdmin } from 'utils/helpers/permissions.helpers';
import * as Styles from './UserManagementPage.styles';

export interface MenuElements {
  roles: ReactNode[];
  organisations: ReactNode[];
  locations: ReactNode[];
}

const generateMenuItem = (id: string, name: string): ReactNode => (
  <MenuItem value={id} key={id}>
    {name}
  </MenuItem>
);

const UserManagementPage = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [menuElements, setMenuElements] = useState<MenuElements>({ roles: [], organisations: [], locations: [] });
  const [searchNeedle, setSearchNeedle] = useState<string>('');
  const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
  const [deleteModalUserId, setDeleteModalUserId] = useState<null | string>(null);

  const [language] = useLanguage();
  const user = useUser();

  const isMkSuperUser = useMemo(
    () => user.isAuthenticated && user[userRolesIndex].includes(Role.MkSystemAdmin) && isInMaxKelsenOrg(user),
    [user],
  );

  const listUsers = useCallback(
    async () => user.isAuthenticated && user.org_id && setUsers(await API.userManagement.listUsers(user.org_id)),
    [user],
  );

  const reloadUsers = useCallback(() => {
    setIsLoading(true);

    // We're faster than the backend, we need to slow down!
    setTimeout(() => {
      listUsers()
        .catch(error => toast.error(language.UserManagement.error + error))
        .finally(() => setIsLoading(false));
    }, 1000);
  }, [language, listUsers]);

  const submitUsers = useCallback(async () => {
    setIsLoading(true);

    try {
      await API.userManagement.updateUsers(users);
      await listUsers();

      toast.success(language.UserManagement.success);
    } catch (error) {
      toast.error(language.UserManagement.error + error);
    }

    setIsLoading(false);
  }, [language, listUsers, users]);

  useEffect(() => {
    if (user.isAuthenticated && user.org_id) {
      Promise.all([
        isInMaxKelsenOrg(user) ? API.userManagement.listTenants() : [],
        API.userManagement.listRoles(user.org_id),
        API.userManagement.listLocations(user.org_id),
        listUsers(),
      ])
        .then(([tenants, roles, locations]) => {
          setMenuElements({
            organisations: tenants.map(({ name, auth0_org_id }) => generateMenuItem(auth0_org_id, name)),
            roles: roles.map(({ id, name }) => generateMenuItem(id, name)),
            locations: locations.map(({ location_id, name }) => generateMenuItem(location_id, name)),
          });
        })
        .finally(() => setIsLoading(false));
    }
  }, [listUsers, user]);

  const updateUser = useCallback((userId: string, newState: Partial<User>) => {
    const formattedNewState = Object.entries(newState).reduce(
      (object, [key, value]) => ({ ...object, [key]: value === 'none' ? null : value }),
      {},
    );

    setUsers(rawUsersState => {
      const userIndex = rawUsersState.findIndex(user => user.user_id === userId);

      const newUserState = [...rawUsersState];

      newUserState[userIndex] = { ...newUserState[userIndex], ...formattedNewState };

      return newUserState;
    });
  }, []);

  if (isLoading)
    return (
      <Page>
        <PageSpinner />
      </Page>
    );

  return (
    <Page>
      <Styles.Container>
        <Styles.HeaderContainer>
          <InputField
            variant="outlined"
            value={searchNeedle}
            placeholder={language.UserManagement.searchPlaceholder}
            onChange={event => setSearchNeedle(event.target.value)}
            InputProps={{
              startAdornment: <SearchOutlined sx={{ color: Theme.greys.GREY2, mr: '1rem' }} />,
            }}
          />
          {(isMkSuperUser || isJnJAdmin(user)) && (
            <Styles.Button onClick={() => setCreateUserModalOpen(true)} endIcon={<Add />}>
              {language.UserManagement.createUser}
            </Styles.Button>
          )}
        </Styles.HeaderContainer>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{language.UserManagement.email}</TableCell>
                <TableCell>{language.UserManagement.role}</TableCell>
                <TableCell>{language.UserManagement.location}</TableCell>
                {isMkSuperUser && <TableCell>{language.UserManagement.organisation}</TableCell>}
                <TableCell>{language.UserManagement.active}</TableCell>
                {isMkSuperUser && <TableCell>{language.UserManagement.delete}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {users
                .filter(({ email }) => searchNeedle === '' || email.indexOf(searchNeedle) > -1)
                .sort((a, b) => a.email.localeCompare(b.email))
                .map(({ auth0_role_id, email, location_id, auth0_org_id, user_id, archived }) => (
                  <Fragment key={user_id}>
                    <TableRow>
                      <TableCell>{email}</TableCell>
                      <TableCell>
                        <DropDown
                          onChange={event =>
                            updateUser(user_id, {
                              auth0_role_id: event.target.value as string,
                            })
                          }
                          defaultValue={'none'}
                          value={auth0_role_id ?? 'none'}
                        >
                          {menuElements.roles}
                        </DropDown>
                      </TableCell>
                      <TableCell>
                        <DropDown
                          onChange={event =>
                            updateUser(user_id, {
                              location_id: event.target.value as string,
                            })
                          }
                          defaultValue={'none'}
                          value={location_id ?? 'none'}
                        >
                          {menuElements.locations}
                        </DropDown>
                      </TableCell>
                      {isMkSuperUser && (
                        <TableCell>
                          <DropDown
                            onChange={event =>
                              updateUser(user_id, {
                                auth0_org_id: event.target.value as string,
                              })
                            }
                            defaultValue={'none'}
                            value={auth0_org_id ?? 'none'}
                          >
                            {menuElements.organisations}
                          </DropDown>
                        </TableCell>
                      )}
                      <TableCell>
                        <Checkbox
                          // Archived is the opposite of active, so invert
                          checked={!archived}
                          onChange={() =>
                            updateUser(user_id, {
                              // But we also want to send the opposite of the state here
                              archived: !archived,
                            })
                          }
                        />
                      </TableCell>
                      {isMkSuperUser && (
                        <TableCell>
                          <IconButton onClick={() => setDeleteModalUserId(user_id)}>
                            <DeleteOutlined />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                    <DeleteUserModal
                      open={user_id === deleteModalUserId}
                      onClose={() => setDeleteModalUserId(null)}
                      onSubmit={() => {
                        setDeleteModalUserId(null);
                        reloadUsers();
                      }}
                      user={{
                        email,
                        id: user_id,
                      }}
                    />
                  </Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Styles.FooterContainer>
          <Styles.Button onClick={submitUsers}>{language.UserManagement.submitChanges}</Styles.Button>
        </Styles.FooterContainer>
        <CreateUserModal
          open={createUserModalOpen}
          onClose={() => setCreateUserModalOpen(false)}
          onSubmit={() => {
            setCreateUserModalOpen(false);
            reloadUsers();
          }}
          menuElements={menuElements}
        />
      </Styles.Container>
    </Page>
  );
};
export default UserManagementPage;
